import { IconFileTypeCsv } from '@tabler/icons-react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { MRT_RowData } from 'mantine-react-table';
import React, { useCallback } from 'react';
import { showToast } from 'src/utils';
import { TableToolbarActionIcon } from 'src/design/atoms/TableToolbarActionIcon';
import {
  IExportToolbarActionProps,
  prepareTableExport,
} from 'src/design/organisms/exportToolbarShared';

function ExportCsvToolbarActionInner<TData extends MRT_RowData>(
  { table, transform, filename }: IExportToolbarActionProps<TData>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const handleExportCsv = useCallback(() => {
    const { data, filename: fn } = prepareTableExport(
      table,
      transform,
      filename,
    );

    if (data.length === 0) {
      showToast('No rows to export');
      return;
    }

    const csvConfig = mkConfig({
      fieldSeparator: ',',
      decimalSeparator: '.',
      useKeysAsHeaders: true,
      filename: fn,
    });
    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  }, [table, transform, filename]);

  return (
    <TableToolbarActionIcon
      ref={ref}
      label="Export CSV"
      onClick={handleExportCsv}
      mr={5}
    >
      <IconFileTypeCsv />
    </TableToolbarActionIcon>
  );
}

/** @registry DataTable toolbar icon that exports the current (pre-pagination) row model to a CSV file via export-to-csv. */
export const ExportCsvToolbarAction = React.memo(
  React.forwardRef(ExportCsvToolbarActionInner),
) as (<TData extends MRT_RowData>(
  props: IExportToolbarActionProps<TData> & {
    ref?: React.Ref<HTMLButtonElement>;
  },
) => React.ReactElement) & { displayName?: string };
ExportCsvToolbarAction.displayName = 'ExportCsvToolbarAction';
